import { useEffect, useState, type FormEvent } from "react";
import { AlertTriangle, History } from "lucide-react";
import { formatRatePercent } from "@laundrypro/shared";
import {
  Badge, Button, Card, CardHeader, EmptyState, ErrorText, FormSection,
  Input, PageHeader, Skeleton, Table, Td, Th, THead, Tr, useToast,
} from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { ApiError, apiFetch } from "../lib/api";
import { dateTime } from "../lib/format";

interface TaxSettings {
  vatEnabled: boolean;
  rateBps: number;
  pricesInclusive: boolean;
  effectiveFrom: string;
  createdAt: string;
}

/**
 * VAT configuration.
 *
 * `tax_settings` is historised, insert-only: saving here APPENDS a new
 * effective-dated row rather than overwriting the current one — see
 * migrations/0001_init.sql. That is what lets a past invoice's rate stay
 * explainable forever, which the history table below exists to show.
 */
export function TaxSettingsPage() {
  const { hasPermission } = useAuth();
  const { language } = useBranding();
  const { showToast } = useToast();
  const ar = language === "ar";

  const [current, setCurrent] = useState<TaxSettings | null>(null);
  const [history, setHistory] = useState<TaxSettings[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [vatEnabled, setVatEnabled] = useState(true);
  const [ratePercent, setRatePercent] = useState("15");
  const [pricesInclusive, setPricesInclusive] = useState(true);

  const canEdit = hasPermission("tax_settings.update");

  const load = async () => {
    setLoadError(null);
    try {
      const [c, h] = await Promise.all([
        apiFetch<TaxSettings>("/settings/tax"),
        apiFetch<{ items: TaxSettings[] }>("/settings/tax/history"),
      ]);
      setCurrent(c);
      setHistory(h.items);
      setVatEnabled(c.vatEnabled);
      setRatePercent((c.rateBps / 100).toString());
      setPricesInclusive(c.pricesInclusive);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : ar ? "تعذّر تحميل إعدادات الضريبة." : "Couldn't load tax settings.");
    }
  };

  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    const rateBps = Math.round(parseFloat(ratePercent) * 100);
    if (!Number.isFinite(rateBps) || rateBps < 0 || rateBps > 10000) {
      setSaveError(ar ? "أدخل نسبة صحيحة بين ٠ و١٠٠." : "Enter a valid percentage between 0 and 100.");
      return;
    }
    setSaving(true);
    try {
      await apiFetch("/settings/tax", { method: "PUT", body: { vatEnabled, rateBps, pricesInclusive } });
      showToast("success", ar ? "حُفظت إعدادات الضريبة. تسري على الطلبات والفواتير الجديدة فقط." : "Tax settings saved. Applies to new orders and invoices only.");
      await load();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : ar ? "تعذّر الحفظ." : "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div className="space-y-4">
        <PageHeader title={ar ? "الضريبة" : "Tax"} />
        <Card>
          <EmptyState icon={<AlertTriangle className="size-6" />} title={ar ? "تعذّر التحميل" : "Couldn't load"} description={loadError}
            action={<Button size="sm" onClick={load}>{ar ? "إعادة المحاولة" : "Retry"}</Button>} />
        </Card>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-7 w-40" />
        <Card><Skeleton className="h-64 w-full" /></Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={ar ? "الضريبة" : "Tax"}
        subtitle={ar ? "ضريبة القيمة المضافة. يسري أي تغيير على المستندات الجديدة فقط." : "VAT. Any change applies to new documents only."}
      />

      <form onSubmit={save} className="space-y-4">
        <Card>
          <FormSection legend={ar ? "الإعداد الحالي" : "Current configuration"}>
            <div className="space-y-3">
              <label className="flex cursor-pointer items-center gap-2.5">
                <input type="checkbox" checked={vatEnabled} onChange={(e) => setVatEnabled(e.target.checked)} disabled={!canEdit}
                  className="size-4 rounded border-line-strong text-accent-500" />
                <span className="text-sm text-ink-900">{ar ? "تفعيل ضريبة القيمة المضافة" : "VAT enabled"}</span>
              </label>

              <div className="grid gap-4 sm:grid-cols-2">
                <Input label={ar ? "نسبة الضريبة (%)" : "VAT rate (%)"} type="number" step="0.01" min={0} max={100}
                  value={ratePercent} onChange={(e) => setRatePercent(e.target.value)} disabled={!canEdit || !vatEnabled} required />
              </div>

              <label className="flex cursor-pointer items-center gap-2.5">
                <input type="checkbox" checked={pricesInclusive} onChange={(e) => setPricesInclusive(e.target.checked)} disabled={!canEdit || !vatEnabled}
                  className="size-4 rounded border-line-strong text-accent-500" />
                <span className="text-sm text-ink-900">
                  {ar ? "الأسعار شاملة الضريبة" : "Prices are VAT-inclusive"}
                </span>
              </label>
              <p className="text-xs text-muted-foreground">
                {ar
                  ? "عند التفعيل، تُشتق الضريبة من السعر المعروض بالطرح. عند الإيقاف، تُضاف الضريبة فوق السعر."
                  : "When on, VAT is derived from the displayed price by subtraction. When off, VAT is added on top."}
              </p>
            </div>
          </FormSection>
        </Card>

        {saveError && <ErrorText>{saveError}</ErrorText>}

        {canEdit && (
          <div className="flex justify-end">
            <Button type="submit" loading={saving}>{ar ? "حفظ" : "Save"}</Button>
          </div>
        )}
      </form>

      <Card padding="none">
        <div className="p-5 pb-0 sm:p-6 sm:pb-0">
          <CardHeader title={ar ? "سجل التغييرات" : "Change history"} description={ar ? "كل صف يفسّر ضريبة فاتورة صدرت في تاريخه." : "Each row explains the VAT on an invoice issued at that time."} />
        </div>
        <div className="p-5 pt-3 sm:p-6 sm:pt-3">
          {history.length === 0 ? (
            <EmptyState icon={<History className="size-6" />} title={ar ? "لا يوجد سجل" : "No history yet"} />
          ) : (
            <Table>
              <THead>
                <tr>
                  <Th>{ar ? "النسبة" : "Rate"}</Th>
                  <Th>{ar ? "الأسعار شاملة؟" : "Inclusive?"}</Th>
                  <Th>{ar ? "مفعّلة؟" : "Enabled?"}</Th>
                  <Th>{ar ? "ساري منذ" : "Effective from"}</Th>
                </tr>
              </THead>
              <tbody>
                {history.map((h, i) => (
                  <Tr key={h.effectiveFrom + i}>
                    <Td className="tabular-nums font-medium">{formatRatePercent(h.rateBps)}</Td>
                    <Td><Badge tone={h.pricesInclusive ? "info" : "neutral"}>{h.pricesInclusive ? (ar ? "نعم" : "Yes") : (ar ? "لا" : "No")}</Badge></Td>
                    <Td><Badge tone={h.vatEnabled ? "success" : "neutral"}>{h.vatEnabled ? (ar ? "نعم" : "Yes") : (ar ? "لا" : "No")}</Badge></Td>
                    <Td className="tabular-nums text-muted-foreground">{dateTime(h.effectiveFrom, language)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card>
    </div>
  );
}
